/**
 * KlachtAdviesPanel — het interne tactische advies bij een klacht.
 *
 * Toont positie, compensatielijn, tactische redenering en formulerings-
 * valkuilen (uit messages.ai_klacht_advies). NOOIT voor de klant — het
 * concept-antwoord voor de klant staat los in de composer (aiDraft).
 *
 * Zelf-standig: leest klachtAdvies + generate-actie uit de inbox-store en
 * heeft een eigen extra-context-veld om opnieuw te genereren met een
 * actuele toelichting of besluit van de medewerker.
 *
 * Plan: plans/2026-07-14-klacht-advies-inbox.md
 */

import { useEffect, useState } from 'react';
import { useInboxStore } from '../../stores/inbox-store';

export function KlachtAdviesPanel() {
  const klachtAdvies = useInboxStore(s => s.klachtAdvies);
  const isGenerating = useInboxStore(s => s.isGeneratingKlachtAdvies);
  const generateKlachtAdvies = useInboxStore(s => s.generateKlachtAdvies);
  const conversationId = useInboxStore(s => s.activeThread?.conversation.id);

  const [showContext, setShowContext] = useState(false);
  const [context, setContext] = useState('');
  useEffect(() => {
    setContext('');
    setShowContext(false);
  }, [conversationId]);

  if (!klachtAdvies) return null;

  const regenerate = () => {
    generateKlachtAdvies(context.trim() || undefined);
  };

  return (
    <div className="py-3">
      <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex flex-wrap items-center gap-2">
            <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <span className="text-sm font-semibold text-amber-800">Intern klacht-advies</span>
            <span className="text-xs text-amber-600">niet voor de klant</span>
            {klachtAdvies.schrijfversie && <span className="rounded bg-white px-2 py-0.5 text-xs text-amber-800">Vrije conceptschrijver</span>}
          </div>
        </div>

        {!!klachtAdvies.goedkeuring_nodig?.length && <div className="mb-3 rounded-md border border-amber-400 bg-white p-3 text-sm">
          <p className="font-semibold text-amber-900">Voor jouw besluit</p>
          <p className="mt-1 text-slate-600">Beoordeel deze voorstellen vóór verzending. Ze zijn nog niet uitgevoerd.</p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-slate-800">
            {klachtAdvies.goedkeuring_nodig.map((besluit, i) => <li key={i}>{besluit}</li>)}
          </ul>
        </div>}

        {klachtAdvies.plan && <div className="space-y-2 text-sm mb-3">
          <p className="font-medium text-slate-800">{klachtAdvies.plan.oplossing}</p>
          {klachtAdvies.plan.nodig.length > 0 && <div><span className="font-medium">Nog nodig</span><ul className="list-disc pl-5">{klachtAdvies.plan.nodig.map((n, i) => <li key={i}>{n}</li>)}</ul></div>}
          <p className="text-xs text-slate-500">{[klachtAdvies.plan.order_referentie, klachtAdvies.plan.item_referentie].filter(Boolean).join(' · ')} · zelfde versie als concept en acties</p>
          <details><summary className="cursor-pointer text-amber-800">Bronnen en onderbouwing</summary>
            <p className="mt-2">{klachtAdvies.waarom}</p>
            <ul className="mt-2 space-y-1">{klachtAdvies.plan.bronnen.map((b, i) => <li key={i} className={b.status === 'onbeschikbaar' ? 'text-red-700' : 'text-slate-600'}>
              {b.bron}: {b.status} ({b.aantal}){b.toelichting ? ` · ${b.toelichting}` : ''}
            </li>)}</ul>
            {klachtAdvies.valkuilen.map((v, i) => <p className="mt-1 text-amber-800" key={i}>{v}</p>)}
          </details>
        </div>}
        {!klachtAdvies.plan && <div className="space-y-2 text-sm">
          {klachtAdvies.positie && (
            <div>
              <span className="font-medium text-amber-800">Positie: </span>
              <span className="text-slate-700">{klachtAdvies.positie}</span>
            </div>
          )}
          {klachtAdvies.compensatie && (
            <div>
              <span className="font-medium text-amber-800">Compensatielijn: </span>
              <span className="text-slate-700">{klachtAdvies.compensatie}</span>
            </div>
          )}
          {klachtAdvies.waarom && (
            <div>
              <span className="font-medium text-amber-800">Waarom: </span>
              <span className="text-slate-700">{klachtAdvies.waarom}</span>
            </div>
          )}
          {klachtAdvies.valkuilen.length > 0 && (
            <div>
              <span className="font-medium text-amber-800">Valkuilen:</span>
              <ul className="mt-1 list-disc list-inside space-y-0.5 text-slate-700">
                {klachtAdvies.valkuilen.map((v, i) => (
                  <li key={i}>{v}</li>
                ))}
              </ul>
            </div>
          )}
        </div>}

        <div className="mt-3 pt-3 border-t border-amber-200">
          {!showContext ? (
            <button
              onClick={() => setShowContext(true)}
              className="text-xs font-medium text-amber-700 hover:text-amber-900"
            >
              Opnieuw met extra context
            </button>
          ) : (
            <div className="space-y-2">
              <label htmlFor="klacht-extra-context" className="block text-sm font-medium text-amber-800">Actuele toelichting of jouw besluit</label>
              <textarea
                id="klacht-extra-context"
                rows={4}
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="Voeg de actuele situatie, het vakoordeel of je besluit voor dit dossier toe"
                className="w-full text-sm border border-amber-200 bg-white rounded-lg px-3 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
              <button
                onClick={regenerate}
                disabled={isGenerating}
                className="text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1.5 rounded-lg disabled:opacity-50 flex-shrink-0"
              >
                {isGenerating ? 'Bezig...' : 'Opnieuw'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
